/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { UnitPositionType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OutOfServiceCrewProps {
  id: string;
  personId: string;
  unitPositionId: string;
  positionType: UnitPositionType | null;
  unitPositionRole: string | null;
  isFlightCrew: boolean;
  endDate: Date | null;
  notificationAddress: string | null;
}

export class OutOfServiceCrewRecord extends Record<OutOfServiceCrewProps>({
  id: null as any,
  personId: null as any,
  unitPositionId: null as any,
  positionType: null as any,
  unitPositionRole: null as any,
  isFlightCrew: null as any,
  endDate: null as any,
  notificationAddress: null as any,
}) {
  public static fromJS(json: {
    id: string;
    personId: string;
    unitPositionId: string;
    positionType: UnitPositionType | null;
    unitPositionRole: string | null;
    isFlightCrew: boolean;
    endDate: string | number | Date | null;
    notificationAddress: string | null;
  }): OutOfServiceCrewRecord {
    if (json == null) {
      throw new Error('OutOfServiceCrewRecord.fromJS: parameter cannot be null');
    }
    return new OutOfServiceCrewRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceCrewJson.id',
      ),
      personId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceCrewJson.personId',
      ),
      unitPositionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitPositionId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceCrewJson.unitPositionId',
      ),
      positionType: json.positionType,
      unitPositionRole: json.unitPositionRole,
      isFlightCrew: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isFlightCrew', false),
        'OutOfServiceCrewJson.isFlightCrew',
      ),
      endDate: json.endDate != null ? new Date(json.endDate) : null,
      notificationAddress: json.notificationAddress,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    personId: string;
    unitPositionId: string;
    positionType: UnitPositionType | null;
    unitPositionRole: string | null;
    isFlightCrew: boolean;
    endDate: string | number | Date | null;
    notificationAddress: string | null;
  }): [OutOfServiceCrewRecord, Error[]] {
    if (json == null) {
      throw new Error('OutOfServiceCrewRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OutOfServiceCrewRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceCrewJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      personId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'personId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceCrewJson.personId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitPositionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitPositionId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceCrewJson.unitPositionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionType: tryOrDefault(
        () => json.positionType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitPositionRole: tryOrDefault(
        () => json.unitPositionRole,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isFlightCrew: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isFlightCrew', false),
            'OutOfServiceCrewJson.isFlightCrew',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endDate: tryOrDefault(
        () => (json.endDate != null ? new Date(json.endDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: OutOfServiceCrewProps) {
    super(props);
    if (props == null) {
      throw new Error('OutOfServiceCrewRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OutOfServiceCrewViewModelBase implements OutOfServiceCrewProps {
  protected readonly _props: OutOfServiceCrewProps;
  private _json?: OutOfServiceCrewProps;

  get id(): string {
    return this._props.id;
  }
  get personId(): string {
    return this._props.personId;
  }
  get unitPositionId(): string {
    return this._props.unitPositionId;
  }
  get positionType(): UnitPositionType | null {
    return this._props.positionType;
  }
  get unitPositionRole(): string | null {
    return this._props.unitPositionRole;
  }
  get isFlightCrew(): boolean {
    return this._props.isFlightCrew;
  }
  get endDate(): Date | null {
    return this._props.endDate;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }

  public get isClinician() {
    return this._props.positionType === UnitPositionType.Clinician;
  }
  public get isClinicianTrainee() {
    return this._props.positionType === UnitPositionType.ClinicianTrainee;
  }
  public get isCoPilot() {
    return this._props.positionType === UnitPositionType.CoPilot;
  }
  public get isDriver() {
    return this._props.positionType === UnitPositionType.Driver;
  }
  public get isMechanic() {
    return this._props.positionType === UnitPositionType.Mechanic;
  }
  public get isNurse() {
    return this._props.positionType === UnitPositionType.Nurse;
  }
  public get isObserver() {
    return this._props.positionType === UnitPositionType.Observer;
  }
  public get isOther() {
    return this._props.positionType === UnitPositionType.Other;
  }
  public get isParamedic() {
    return this._props.positionType === UnitPositionType.Paramedic;
  }
  public get isPilot() {
    return this._props.positionType === UnitPositionType.Pilot;
  }
  public get isPilotTrainee() {
    return this._props.positionType === UnitPositionType.PilotTrainee;
  }

  public isAnyPositionType(...unitPositionType: UnitPositionType[]) {
    return this._props.positionType != null && unitPositionType.includes(this._props.positionType);
  }

  constructor(props: OutOfServiceCrewProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        personId: this._props.personId,
        unitPositionId: this._props.unitPositionId,
        positionType: this._props.positionType,
        unitPositionRole: this._props.unitPositionRole,
        isFlightCrew: this._props.isFlightCrew,
        endDate: this._props.endDate,
        notificationAddress: this._props.notificationAddress,
      };
    }
    return this._json;
  }
}
